/**
 * Render a past timestamp as "3 days ago" / "just now" / "yesterday"
 * style text. Used by the City Practice history panel to label past
 * guesses. Locale: English only (matches the rest of the v1 SPA — the
 * i18n pass adds locale plumbing later, see the translation memory).
 *
 * Bands chosen to read naturally rather than to be precise. "Just now"
 * absorbs anything under a minute; "yesterday" is the literal previous
 * calendar day relative to the reference time.
 *
 * Future timestamps (clock skew, server vs client) collapse to "just
 * now" so a slightly-future row never reads as "in 8 minutes" inside
 * a "history" panel.
 */
export function formatRelativeTime(
  pastMs: number,
  nowMs: number = Date.now(),
): string {
  const deltaMs = nowMs - pastMs;
  if (deltaMs < 60_000) return 'just now';

  const minutes = Math.floor(deltaMs / 60_000);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;

  const hours = Math.floor(deltaMs / 3_600_000);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;

  const days = Math.floor(deltaMs / 86_400_000);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} ${months === 1 ? 'month' : 'months'} ago`;

  const years = Math.floor(days / 365);
  return `${years} ${years === 1 ? 'year' : 'years'} ago`;
}
